/**
 * Utilitar pentru verificarea accesului la functionalitati
 * bazat pe planul de abonament
 */

/**
 * Verifica daca utilizatorul are acces la o functionalitate
 */
export const hasFeature = (featureCode: string, features: string[] = []): boolean => {
  return features.includes(featureCode);
};

/**
 * Constante pentru feature codes - Meniuri
 */
export const MENU_FEATURES = {
  // Management
  PACIENTI: 'menu.pacienti.view',
  PROGRAMARI: 'menu.programari.view',
  DOCTORI: 'menu.doctori.view',
  CLIENTI: 'menu.clienti.view',
  
  // Servicii & Financiar
  SERVICII: 'menu.servicii.view',
  RETETE: 'menu.retete.view',
  MEDICAMENTE: 'menu.medicamente.view',
  FACTURARE: 'menu.facturare.view',
  
  // Sistem & Setari
  NOTIFICARI: 'menu.notificari.view',
  PROFIL_CLINICA: 'menu.profil_clinica.view',
  SETARI_INTERNE: 'menu.setari_interne.view'
} as const;

/**
 * Constante pentru feature codes - Actiuni
 */
export const ACTION_FEATURES = {
  // Pacienti
  PACIENTI_CREATE: 'pacienti.create',
  PACIENTI_UPDATE: 'pacienti.update',
  PACIENTI_DELETE: 'pacienti.delete',
  
  // Programari
  PROGRAMARI_CREATE: 'programari.create',
  PROGRAMARI_UPDATE: 'programari.update',
  PROGRAMARI_CANCEL: 'programari.cancel',
  
  // Servicii
  SERVICII_MANAGE: 'servicii.manage',
  
  // Retete
  RETETE_ISSUE: 'retete.issue',
  
  // Medicamente
  MEDICAMENTE_STOCK_MANAGE: 'medicamente.stock.manage',
  
  // Facturare
  FACTURARE_ISSUE: 'facturare.issue',
  FACTURARE_EXPORT: 'facturare.export',
  
  // Notificari
  NOTIFICARI_MANAGE: 'notificari.manage',
  
  // Setari
  SETARI_MANAGE: 'setari.manage'
} as const;

/**
 * Lista completa de features disponibile
 */
export const ALL_FEATURES = {
  ...MENU_FEATURES,
  ...ACTION_FEATURES
};

/**
 * Descrieri pentru features (pentru UI)
 */
export const FEATURE_DESCRIPTIONS: Record<string, string> = {
  // Meniuri
  'menu.pacienti.view': 'Vizualizare si gestionare pacienti',
  'menu.programari.view': 'Vizualizare si gestionare programari',
  'menu.doctori.view': 'Vizualizare si gestionare doctori',
  'menu.clienti.view': 'Vizualizare si gestionare clienti',
  'menu.servicii.view': 'Vizualizare si gestionare servicii',
  'menu.retete.view': 'Vizualizare si gestionare retete',
  'menu.medicamente.view': 'Vizualizare si gestionare medicamente',
  'menu.facturare.view': 'Vizualizare si gestionare facturare',
  'menu.notificari.view': 'Vizualizare si gestionare notificari',
  'menu.profil_clinica.view': 'Vizualizare si editare profil clinica',
  'menu.setari_interne.view': 'Acces setari interne avansate',
  
  // Actiuni
  'pacienti.create': 'Adaugare pacienti noi',
  'pacienti.update': 'Editare informatii pacienti',
  'pacienti.delete': 'Stergere pacienti',
  'programari.create': 'Creare programari noi',
  'programari.update': 'Editare programari',
  'programari.cancel': 'Anulare programari',
  'servicii.manage': 'Gestionare completa servicii',
  'retete.issue': 'Emitere retete',
  'medicamente.stock.manage': 'Gestionare stoc medicamente',
  'facturare.issue': 'Emitere facturi',
  'facturare.export': 'Export rapoarte financiare',
  'notificari.manage': 'Gestionare notificari si alerte',
  'setari.manage': 'Gestionare setari avansate'
};

/**
 * Bundle-uri de features pentru planuri
 */
export const FEATURE_BUNDLES = {
  BASIC: [
    'menu.pacienti.view',
    'menu.programari.view',
    'menu.clienti.view',
    'menu.facturare.view',
    'menu.profil_clinica.view',
    'pacienti.create',
    'pacienti.update',
    'programari.create',
    'programari.update',
    'programari.cancel',
    'facturare.issue'
  ],
  
  PROFESSIONAL: [
    'menu.pacienti.view',
    'menu.programari.view',
    'menu.clienti.view',
    'menu.facturare.view',
    'menu.profil_clinica.view',
    'menu.doctori.view',
    'menu.servicii.view',
    'menu.retete.view',
    'menu.medicamente.view',
    'menu.notificari.view',
    'pacienti.create',
    'pacienti.update',
    'pacienti.delete',
    'programari.create',
    'programari.update',
    'programari.cancel',
    'servicii.manage',
    'retete.issue',
    'medicamente.stock.manage',
    'facturare.issue',
    'facturare.export',
    'notificari.manage'
  ],
  
  ENTERPRISE: [
    'menu.pacienti.view',
    'menu.programari.view',
    'menu.clienti.view',
    'menu.facturare.view',
    'menu.profil_clinica.view',
    'menu.doctori.view',
    'menu.servicii.view',
    'menu.retete.view',
    'menu.medicamente.view',
    'menu.notificari.view',
    'menu.setari_interne.view',
    'pacienti.create',
    'pacienti.update',
    'pacienti.delete',
    'programari.create',
    'programari.update',
    'programari.cancel',
    'servicii.manage',
    'retete.issue',
    'medicamente.stock.manage',
    'facturare.issue',
    'facturare.export',
    'notificari.manage',
    'setari.manage',
    'api.access',
    'integrations.manage',
    'reports.custom',
    'automation.workflows',
    'multi_location.manage'
  ]
};

/**
 * Grupare features pentru UI
 */
export const FEATURE_GROUPS = [
  {
    name: 'Management',
    features: [
      'menu.pacienti.view',
      'menu.programari.view',
      'menu.doctori.view',
      'menu.clienti.view'
    ]
  },
  {
    name: 'Servicii & Financiar',
    features: [
      'menu.servicii.view',
      'menu.retete.view',
      'menu.medicamente.view',
      'menu.facturare.view'
    ]
  },
  {
    name: 'Sistem & Setari',
    features: [
      'menu.notificari.view',
      'menu.profil_clinica.view',
      'menu.setari_interne.view'
    ]
  },
  {
    name: 'Actiuni Pacienti',
    features: [
      'pacienti.create',
      'pacienti.update',
      'pacienti.delete'
    ]
  },
  {
    name: 'Actiuni Programari',
    features: [
      'programari.create',
      'programari.update',
      'programari.cancel'
    ]
  },
  {
    name: 'Actiuni Avansate',
    features: [
      'servicii.manage',
      'retete.issue',
      'medicamente.stock.manage',
      'facturare.issue',
      'facturare.export',
      'notificari.manage',
      'setari.manage'
    ]
  }
];

